/**
 * "EXPLAIN THIS" FOR A VILLAGE PLAN, AS AGED CARE TELLS IT (owner, 22 Oct 2026: "the UI for the explain this does
 * not match the updates that were carried out for aged care … all modules use consistent UI").
 *
 * The old "Your plan summary" card (RvPlanRecap — paragraphs, before/after rows and a "why" under each) is
 * replaced in the Explain dialog by aged care's ledger: numbered lines, each saying what is done and what that
 * does to the numbers, then "Put together" with the money story inside it, then the "=" line against staying
 * put (or, once the family has decided to move, against the best way to move — RvPlansView's rule).
 *
 * Presentation only. Every figure is one the page already holds: the plan's snapshot or the hand-built plan's
 * own figures, staying put's, the contract's departure fee (exitAfter), the rent on the candidate's inputs and
 * the investment income on the plan's first year in the village. Wording reused from RvPlanRecap where it was
 * right, in plain words; the household is "you" for the client and named for the adviser (householdVoice).
 */
import React from 'react';
import { CauseEffectLedger, FromTo, putTogetherLine } from '../../components/strategy/CauseEffectLedger.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { inReaderWords, type HouseholdVoice } from '../../utils/householdWords.ts';
import { EXTRA_ALLOWABLE_AMOUNT } from '../../constants/index.ts';

export interface RvExplainFigures {
    legacy: number;
    pension: number;
    cashflow: number;
    /** The year's investment income (interest, dividends, rent), when the rows carry it. */
    investmentIncome?: number | null;
}

export interface RvCauseEffectProps {
    voice: HouseholdVoice;
    /** The plan's own words (rvPlanWords): the line under the card's title, which says how the entry payment is paid. */
    sub: string;
    /** 'liquid' | 'rent_home' | 'sell_home' | 'investment_property' — the candidate's fundFrom. */
    fundFrom: string;
    rentHome: boolean;
    /** The household owns a home today. */
    hasHome: boolean;
    homeValue: number;
    entryFee: number;
    /** A sale's price and selling costs (sellHomeDetails), for the first line. */
    salePrice?: number;
    /** The weekly rent after the weeks it sits empty, and the year's costs of letting it. */
    rentWeekly?: number;
    rentCosts?: number;
    /** How much of the entry payment the chosen funding does not cover (a hand-built plan only). */
    shortfall?: number;
    /** A guaranteed income that returns nothing to the family. */
    nilAnnuity?: { amount: number; ownerName: string } | null;
    villageFeesAYear: number;
    departureFee: number;
    backAfter: number;
    rentAssistance?: number;
    plan: RvExplainFigures;
    /** Staying put — the Age Pension, cashflow and investment income are always read against it. */
    stayPut: RvExplainFigures | null;
    /** What the "=" line is measured against: staying put, or the best way to move once decided. */
    benchmark: { legacy: number; word: string } | null;
    horizonYears: number;
    moneyStory?: React.ReactNode;
    children?: React.ReactNode;
    /** The plan's pieces, explained above the lines (owner, 24 Oct 2026). */
    leverKinds?: ReadonlyArray<string>;
}

const money = (n: number) => formatCurrency(Math.abs(Math.round(n)));
const B = ({ children }: { children: React.ReactNode }) => <strong className="text-slate-900">{children}</strong>;
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** The year's investment income, from the cashflow lines the engine tags as investment (interest, dividends, rent). */
export function rvInvestmentIncomeOf(rows: ReadonlyArray<{ annualVillageCost?: unknown; financials?: { cashFlowBreakdown?: Array<{ value?: unknown; series?: unknown }> } | null }> | null | undefined, inVillage = true): number | null {
    const list = rows || [];
    // The first year in the unit, as the card's snapshot reads it (a dated move's first row is the year before it).
    const row = (inVillage ? list.find((r) => (Number(r?.annualVillageCost) || 0) > 0) : null) ?? list[0];
    const lines = (row?.financials?.cashFlowBreakdown || []).filter((l) => l?.series === 'investment');
    if (!lines.length) return null;
    return lines.reduce((t, l) => t + (Number(l.value) || 0), 0);
}

export const RvCauseEffect = ({
    voice: v, sub, fundFrom, rentHome, hasHome, homeValue, entryFee, salePrice = 0, rentWeekly = 0, rentCosts = 0, shortfall = 0, nilAnnuity = null,
    villageFeesAYear, departureFee, backAfter, rentAssistance = 0, plan, stayPut, benchmark, horizonYears, moneyStory = null, children, leverKinds = [],
}: RvCauseEffectProps) => {
    const yrs = `${horizonYears} ${horizonYears === 1 ? 'year' : 'years'}`;
    const sells = fundFrom === 'sell_home';
    const keepsHome = hasHome && !sells;
    const rented = keepsHome && (rentHome || fundFrom === 'rent_home');
    const rentNet = Math.max(0, rentWeekly * 52 - rentCosts);
    const lines: React.ReactNode[] = [];

    // A hand-built plan that does not yet pay the whole entry payment says so first: everything after it assumes the move.
    if (Math.round(shortfall) > 0) {
        lines.push(<span data-testid="rv-cause-effect-unfunded"><B>This plan is not paid for yet.</B> The savings chosen cover {money(Math.max(0, entryFee - shortfall))} of the {money(entryFee)} entry payment, leaving {money(shortfall)} to find, and a village entry payment has to be paid in full. The lines below assume the move goes ahead.</span>);
    }

    // 1. What is done, and how the entry payment is paid (the card's own sub-line says where it comes from).
    const doSentence = sells
        ? `${v.They} sell the home${salePrice > 0 ? ` for ${money(salePrice)}` : ''} and move into the village.`
        : fundFrom === 'investment_property'
            ? `${v.They} sell the investment property and move into the village.`
            : rented
                ? `${v.They} keep the home, rent it out at ${money(rentWeekly)} a week, and move into the village.`
                : keepsHome
                    ? `${v.They} keep the home as it is and move into the village.`
                    : `${v.They} pay the ${money(entryFee)} entry payment and move into the village.`;
    // The card's sub-line repeats the rent; said once here, with what it brings in (live check, 22 Oct 2026).
    lines.push(<><B>{doSentence}</B> {inReaderWords(rented ? sub.replace(/\s*The home is let at [^.]*\./i, '') : sub, v)}{rented && rentNet > 0 ? <> After the weeks it sits empty and {money(rentCosts)} a year of costs, the rent brings in about {money(rentNet)} a year.</> : null}</>);

    // 2. Homeowner or not: the Extra Allowable Amount decides it, and with it whether the entry payment is counted.
    const homeowner = entryFee > EXTRA_ALLOWABLE_AMOUNT;
    // A couple are both named, so the noun is plural (review, 18 Oct 2026: "treats John as a homeowner" on John and Kerry's file).
    const whoWord = v.couple ? (homeowner ? 'homeowners' : 'non-homeowners') : (homeowner ? 'a homeowner' : 'a non-homeowner');
    lines.push(
        <span data-testid="rv-cause-effect-eaa">
            <B>The entry payment is {homeowner ? 'above' : 'at or below'} {money(EXTRA_ALLOWABLE_AMOUNT)}, so Centrelink treats {v.names} as {whoWord}.</B>{' '}
            That figure, the Extra Allowable Amount, is the line Centrelink uses to decide whether a village unit counts as {v.their} home.{' '}
            {homeowner
                ? 'Above it, the entry payment itself is not counted, but the lower limits for homeowners apply to everything else.'
                : `At or below it, the entry payment is counted as an asset, but the higher limits for people without a home apply, and Rent Assistance may be paid on the village fees.`}
        </span>,
    );

    // 3. The home kept is counted straight away — the village has no two-year exemption.
    if (keepsHome) {
        lines.push(<><B>The home {v.they} keep is counted in full{rented ? ', and the rent as income' : ''}.</B> Once the unit is {v.their} home, the old one is an asset like any other: unlike moving into care, a village move has no two-year exemption.</>);
    }

    // A guaranteed income that returns nothing: the main reason the family is left less, said where it happens.
    if (nilAnnuity && nilAnnuity.amount > 0) {
        lines.push(<><B>The {money(nilAnnuity.amount)} guaranteed income returns nothing to the family</B> when {nilAnnuity.ownerName} {nilAnnuity.ownerName === 'you' ? 'die' : 'dies'}. That money leaves the estate the day it is bought, and it is most of the difference below.</>);
    }

    if (stayPut) {
        // 4. The Age Pension, before and after, and why.
        const dp = plan.pension - stayPut.pension;
        const why = rented ? 'the rented home and its rent are counted' : keepsHome ? 'the home is counted' : sells || fundFrom === 'investment_property' ? 'the sale money is counted, wherever it is put' : null;
        lines.push(
            <span data-testid="rv-cause-effect-pension">
                {Math.abs(dp) < 1
                    ? <B>{cap(v.possessive)} Age Pension stays at {money(plan.pension)} a year.</B>
                    : dp > 0
                        ? <><B>Less is counted, so {v.possessive} Age Pension rises</B> <FromTo from={stayPut.pension} to={plan.pension} unit=" a year" better="up" />.</>
                        : <><B>More is counted, so {v.possessive} Age Pension {plan.pension < 1 ? 'stops' : 'falls'}</B> {plan.pension < 1 ? <>(it was {money(stayPut.pension)} a year)</> : <FromTo from={stayPut.pension} to={plan.pension} unit=" a year" better="up" />}{why ? <> — {why}</> : null}.</>}
            </span>,
        );
    }

    // 5. The village's costs: the fees every year, the departure fee when they leave.
    lines.push(
        <span data-testid="rv-cause-effect-fees">
            <B>{villageFeesAYear > 0 ? <>The village fees of {money(villageFeesAYear)} a year start</> : <>This village charges no ongoing fees</>}</B>
            {villageFeesAYear > 0 ? <>, part of the money out every year{rentAssistance > 0 ? <>; {money(rentAssistance)} a year of Rent Assistance helps with them</> : null}.</> : '.'}{' '}
            {departureFee > 0
                ? <>When {v.they} leave, the village keeps a departure fee from the entry payment, about {money(departureFee)} after {yrs} on this contract, and pays back about {money(backAfter)}. That fee is already taken off what the family is left.</>
                : <>This contract has no departure fee, so the entry payment comes back when {v.they} leave.</>}
        </span>,
    );

    if (stayPut) {
        // 6. Investment income: the entry payment no longer earns; a sale's money or the rent may.
        if (plan.investmentIncome != null && stayPut.investmentIncome != null) {
            const di = plan.investmentIncome - stayPut.investmentIncome;
            if (Math.abs(di) >= 100) {
                lines.push(di < 0
                    ? <><B>Less money stays invested, so investment income falls</B> <FromTo from={stayPut.investmentIncome} to={plan.investmentIncome} unit=" a year" better="up" />. The entry payment is money that no longer earns.</>
                    : <><B>Investment income rises</B> <FromTo from={stayPut.investmentIncome} to={plan.investmentIncome} unit=" a year" better="up" />{rented ? ' — the rent is part of it' : sells ? ' — what is left of the sale is invested' : ''}.</>);
            }
        }
        // 7. Put together, with the money story inside it when a year falls short.
        lines.push(putTogetherLine({ cashflow: plan.cashflow, baseCashflow: stayPut.cashflow, they: v.they, baseWord: 'staying put', moneyStory }));
    } else if (moneyStory) {
        lines.push(<>{moneyStory}</>);
    }

    return (
        <CauseEffectLedger
            lines={lines}
            estate={plan.legacy}
            baseEstate={benchmark ? benchmark.legacy : null}
            horizonYears={horizonYears}
            baseWord={benchmark?.word ?? 'staying put'}
            testId="rv-cause-effect"
            leverKinds={leverKinds}
        >
            {children}
        </CauseEffectLedger>
    );
};

export default RvCauseEffect;
